"use client";

import Link from "next/link";
import Image from "next/image";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { createClient } from "@/lib/supabase/client";
import { ClanokVerejny } from "@/types/database";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Calendar,
  Clock,
  ChevronDown,
  ChevronUp,
  Newspaper,
} from "lucide-react";

interface ClankyMegaMenuProps {
  isOpen: boolean;
  onClose: () => void;
  isMobile?: boolean;
}

const MENU_CONTAINER_CLASSES = cn(
  "absolute top-full w-[calc(100vw-2rem)] max-w-7xl",
  "left-1/2 -translate-x-1/2 bg-background border border-border",
  "shadow-2xl z-50 rounded-b-xl animate-in fade-in",
  "slide-in-from-top-2 duration-200",
);

export default function ClankyMegaMenu({
  isOpen,
  onClose,
  isMobile = false,
}: ClankyMegaMenuProps) {
  const LATEST_ARTICLES_TITLE = "Najnovšie články";
  const MORE_TEXT = "Viac";
  const LESS_TEXT = "Menej";
  const ALL_ARTICLES_TEXT = "Zobraziť všetky články";
  const POPULAR_TOPICS_TITLE = "Populárne témy";
  const ARCHIVE_TITLE = "Archív";
  const STATS_TITLE = "Štatistiky";
  const TOTAL_ARTICLES_LABEL = "Celkovo článkov:";
  const ACTIVE_AUTHORS_LABEL = "Aktívni autori:";
  const NO_ARTICLES_TEXT = "Zatiaľ žiadne články";
  const MOBILE_MENU_TITLE = "Články";

  const [clanky, setClanky] = useState<ClanokVerejny[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (isOpen) {
      nacitatClanky();
    }
  }, [isOpen]);

  const nacitatClanky = async () => {
    try {
      const supabase = createClient();
      const { data, error } = await supabase
        .from("clanky_verejne")
        .select("*")
        .order("datum_cas", { ascending: false })
        .limit(6);

      if (error) {
        // Chyba pri načítaní článkov
        return;
      }

      setClanky(data || []);
    } catch {
      // Všeobecná chyba pri načítaní
    } finally {
      setIsLoading(false);
    }
  };

  const formatDatum = (datum: string) => {
    return new Date(datum).toLocaleDateString("sk-SK", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const ziskatNajnovsieClanky = () => {
    return isExpanded ? clanky : clanky.slice(0, 3);
  };

  if (!isOpen) return null;

  const content = (
    <>
      {/* Najnovšie články */}
      <div className="min-w-0 overflow-hidden">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Newspaper className="w-5 h-5 text-status-info shrink-0" />
            <span className="truncate">{LATEST_ARTICLES_TITLE}</span>
          </h3>
          {clanky.length > 3 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-1 text-sm text-primary hover:text-primary/80 transition-colors shrink-0"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  <span className="hidden sm:inline">{LESS_TEXT}</span>
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  <span className="hidden sm:inline">{MORE_TEXT}</span>
                </>
              )}
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : ziskatNajnovsieClanky().length > 0 ? (
          <div className="space-y-4">
            {ziskatNajnovsieClanky().map((clanok) => (
              <Link
                key={clanok.id}
                href={`/clanky/${clanok.slug}`}
                onClick={onClose}
                className="block group hover:bg-accent rounded-lg p-3 transition-colors"
              >
                <div className="flex items-start gap-3 sm:gap-4 min-w-0 overflow-hidden">
                  {clanok.obrazok_url && (
                    <div className="shrink-0">
                      <div className="relative w-16 h-16 rounded-lg overflow-hidden">
                        <Image
                          src={clanok.obrazok_url}
                          alt={clanok.titulok}
                          fill
                          className="object-cover group-hover:scale-105 transition-transform"
                        />
                      </div>
                    </div>
                  )}
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <h4 className="font-medium text-foreground group-hover:text-primary transition-colors line-clamp-2 mb-1 wrap-break-word">
                      {clanok.titulok}
                    </h4>
                    {clanok.perex && (
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-2 wrap-break-word">
                        {clanok.perex}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-x-3 sm:gap-x-4 gap-y-1 text-xs text-muted-foreground mb-2">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3 shrink-0" />
                        <span className="truncate max-w-[150px]">
                          {formatDatum(clanok.datum_cas)}
                        </span>
                      </div>
                      {clanok.cas_citania > 0 && (
                        <div className="flex items-center gap-1 whitespace-nowrap">
                          <Clock className="w-3 h-3 shrink-0" />
                          {clanok.cas_citania} min
                        </div>
                      )}
                      <div className="flex items-center gap-1 sm:gap-1.5 min-w-0">
                        <span className="font-medium truncate">
                          {clanok.autor}
                        </span>
                      </div>
                    </div>
                    {clanok.kategorie.length > 0 && (
                      <div className="flex flex-wrap gap-1 sm:gap-1.5 overflow-hidden">
                        {clanok.kategorie
                          .slice(0, 2)
                          .map((kategoria, index) => (
                            <Badge
                              key={index}
                              variant="secondary"
                              className="text-xs bg-primary/10 text-primary truncate max-w-[120px]"
                            >
                              {kategoria}
                            </Badge>
                          ))}
                        {clanok.kategorie.length > 2 && (
                          <span className="text-xs text-muted-foreground">
                            +{clanok.kategorie.length - 2}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 px-4">
            <Newspaper className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-muted-foreground">{NO_ARTICLES_TEXT}</p>
          </div>
        )}
      </div>

      <div className="space-y-6 min-w-0 overflow-hidden">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-3 truncate">
            {ARCHIVE_TITLE}
          </h3>
          <Button asChild className="w-full h-10 rounded-lg shadow-sm">
            <Link href="/clanky" onClick={onClose}>
              {ALL_ARTICLES_TEXT}
            </Link>
          </Button>
        </div>
        {clanky.length > 0 && (
          <div className="overflow-hidden">
            <h3 className="text-lg font-semibold text-foreground mb-3 truncate">
              {POPULAR_TOPICS_TITLE}
            </h3>
            <div className="flex flex-wrap gap-2 sm:gap-3">
              {Array.from(
                new Set(clanky.flatMap((clanok) => clanok.tagy || [])),
              )
                .slice(0, 8)
                .map((tag, index) => (
                  <Badge
                    key={index}
                    variant="outline"
                    className="text-xs cursor-pointer hover:bg-accent hover:border-primary transition-colors truncate max-w-[150px]"
                  >
                    #{tag}
                  </Badge>
                ))}
            </div>
          </div>
        )}
        <div className="overflow-hidden">
          <h3 className="text-lg font-semibold text-foreground mb-3 truncate">
            {STATS_TITLE}
          </h3>
          <div className="space-y-2 text-sm text-muted-foreground font-medium">
            <div className="flex justify-between gap-2">
              <span className="truncate">{TOTAL_ARTICLES_LABEL}</span>
              <span className="font-black shrink-0 text-foreground">
                {clanky.length}
              </span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="truncate">{ACTIVE_AUTHORS_LABEL}</span>
              <span className="font-black shrink-0 text-foreground">
                {new Set(clanky.map((c) => c.autor)).size}
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  if (isMobile) {
    return (
      <div className="w-full">
        <div className="border-t pt-4 mt-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">{MOBILE_MENU_TITLE}</h2>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <ChevronUp className="w-4 h-4" />
            </Button>
          </div>
          <div className="space-y-6">{content}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={MENU_CONTAINER_CLASSES}>
      <div className="max-h-[80vh] overflow-y-auto overflow-x-hidden">
        <div className="w-full px-4 sm:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_280px] gap-10">
              {content}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
